import { useListaDeOs, usePendenciasDeOs } from '../../../api/queries/ordensDeServico'
import { useSessao } from '../../../auth/contexto'
import { Botao, BotaoLink } from '../../../components/Botao'
import { AvisoDeErro, Esqueleto, EstadoVazio } from '../../../components/Estados'
import { Explicacao } from '../../../components/Explicacao'
import { IconeBusca, IconeNovo, IconeOrdemServico } from '../../../components/Icones'
import { Paginacao } from '../../../components/Paginacao'
import { podeEscreverOs, podeVerPendencias } from '../acessoDaOs'
import { CartaoDeOs } from './CartaoDeOs'
import { DESCRICAO_DA_LISTA } from './colunasDaOs'
import { FiltrosDeOs } from './FiltrosDeOs'
import { MesesSemOrdem } from './MesesSemOrdem'
import { TabelaDeOs } from './TabelaDeOs'
import { TERMOS_DA_OS } from '../termosDaOs'
import { OS } from '../textosDaOs'
import { useFiltrosDeOsNaUrl } from './filtros'

const NOME_DO_ITEM = { singular: 'ordem de serviço', plural: 'ordens de serviço' }

/**
 * As ordens de serviço do contrato.
 *
 * **A tela é dos dois lados do contrato.** Registrar e revogar é ato da CONTRATANTE (4.75, p.29),
 * mas a ordem obriga a contratada — o prazo de execução corre da entrega ao preposto (6.24.6, p.45)
 * e a indisponibilidade da equipe ao receber a ordem vale 10 pontos de IAD (ANEXO VIII, item 4,
 * p.157). Esconder a lista de quem ela obriga seria esconder a obrigação. O que muda de um lado
 * para o outro é o que se pode fazer, não o que se pode ver: `acessoDaOs.ts`.
 *
 * **A faixa dos meses sem ordem vem antes de tudo**, inclusive dos filtros. Ela é a única parte
 * desta tela que fala do que NÃO existe, e o que não existe não aparece em lista nenhuma: junho,
 * julho e agosto de 2026 estão descobertos e até aqui nenhuma tela do sistema dizia isso.
 *
 * @returns a listagem
 */
export function PaginaOrdensDeServico() {
  const { filtros, alterar, limpar, temFiltro } = useFiltrosDeOsNaUrl()
  const { usuario } = useSessao()
  const podeRegistrar = podeEscreverOs(usuario)
  const { data: pagina, isPending, isError, error } = useListaDeOs(filtros)
  const { data: descobertos } = usePendenciasDeOs(podeVerPendencias(usuario))

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="titulo-da-pagina flex items-center gap-1.5">
            {OS.titulo}
            <Explicacao
              titulo={TERMOS_DA_OS.ordemDeServico.rotulo}
              texto={TERMOS_DA_OS.ordemDeServico.ajuda}
              className="h-5 w-5"
            />
          </h1>
          <p className="mt-1 max-w-prose text-sm text-slate-600">{OS.vazio.ajuda}</p>
        </div>
        {podeRegistrar && (
          <BotaoLink to="/ordens-servico/nova" aparencia="primario">
            <IconeNovo className="h-4 w-4" />
            {OS.registrar}
          </BotaoLink>
        )}
      </header>

      {descobertos && <MesesSemOrdem meses={descobertos} podeRegistrar={podeRegistrar} />}

      <FiltrosDeOs filtros={filtros} alterar={alterar} limpar={limpar} temFiltro={temFiltro} />

      {isPending && (
        <div className="cartao px-4 py-5">
          <Esqueleto linhas={6} />
        </div>
      )}
      {isError && <AvisoDeErro erro={error} titulo="Não foi possível carregar as ordens de serviço" />}

      {pagina && pagina.conteudo.length === 0 && (
        <EstadoVazio
          icone={temFiltro ? <IconeBusca className="h-9 w-9" /> : <IconeOrdemServico className="h-9 w-9" />}
          titulo={temFiltro ? OS.semResultados.titulo : OS.vazio.titulo}
          ajuda={temFiltro ? OS.semResultados.ajuda : `${OS.vazio.ajuda} ${OS.vazio.ondeEstaOHistorico}`}
          acao={
            temFiltro ? (
              <Botao onClick={limpar}>{OS.limparFiltros}</Botao>
            ) : (
              podeRegistrar && (
                <BotaoLink to="/ordens-servico/nova" aparencia="primario">
                  <IconeNovo className="h-4 w-4" />
                  {OS.registrar}
                </BotaoLink>
              )
            )
          }
        />
      )}

      {pagina && pagina.conteudo.length > 0 && (
        <>
          {/* Tabela no computador, cartões no telefone. A troca é por CSS e não por `matchMedia`:
              medir a largura em JavaScript faz a página montar com a forma errada e trocar depois
              que o React roda. Com `hidden`, o que está escondido some também da árvore de
              acessibilidade — quem usa leitor de tela ouve uma lista, não as duas. */}
          <div className="hidden sm:block">
            <TabelaDeOs itens={pagina.conteudo} />
          </div>
          <ul aria-label={DESCRICAO_DA_LISTA} className="space-y-3 sm:hidden">
            {pagina.conteudo.map((ordem) => (
              <CartaoDeOs key={ordem.id} ordem={ordem} />
            ))}
          </ul>

          <Paginacao
            pagina={filtros.pagina}
            totalPaginas={pagina.totalPaginas}
            totalElementos={pagina.totalElementos}
            aoMudar={(numero) => alterar({ pagina: numero })}
            nomeDoItem={NOME_DO_ITEM}
          />
        </>
      )}
    </div>
  )
}
